import React, { useState, useEffect, useMemo } from 'react';
import { TMDB_IMAGE_BASE, TRANSLATIONS } from '../constants';
import { Film, AlertCircle } from 'lucide-react';
import { SupportedLang } from '../types';

interface FrameDisplayProps {
  images: string[];
  currentAttempt: number;
  lang: SupportedLang;
}

const FrameDisplay: React.FC<FrameDisplayProps> = ({ images, currentAttempt, lang }) => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [hasImageError, setHasImageError] = useState(false);

  // Select the current frame based on the attempt
  const currentImage = useMemo(() => images[currentAttempt] || images[0], [images, currentAttempt]);

  // Construct a absolute URL. Handle cases where the path might already be absolute or needs a prefix.
  const imageUrl = useMemo(() => {
    if (!currentImage) return '';
    if (currentImage.startsWith('http')) return currentImage;
    // Ensure we have a leading slash for the file path if missing
    const path = currentImage.startsWith('/') ? currentImage : `/${currentImage}`;
    return `${TMDB_IMAGE_BASE}${path}`;
  }, [currentImage]);

  // Reset loading/error state when the image changes
  useEffect(() => {
    setIsImageLoading(true);
    setHasImageError(false);
  }, [imageUrl]);

  return (
    <div className="relative w-full aspect-video bg-zinc-100 dark:bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-zinc-200 dark:border-zinc-800 group lg:max-h-[450px] lg:max-h-[45vh]">
      {/* Loading Shimmer State */}
      {isImageLoading && !hasImageError && (
        <div className="absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-zinc-100 dark:bg-zinc-900">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
          <div className="flex flex-col items-center gap-3">
             <Film className="w-8 h-8 text-zinc-300 dark:text-zinc-800 animate-pulse" />
             <span className="text-[10px] font-heading tracking-[0.2em] text-zinc-400 dark:text-zinc-700 uppercase">
               {TRANSLATIONS.developingReel[lang]}
             </span>
          </div>
        </div>
      )}

      {/* Error Fallback State */}
      {hasImageError && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950 p-6 text-center">
          <AlertCircle className="w-10 h-10 text-red-500/50 mb-4" />
          <span className="text-xs font-bold text-zinc-400 dark:text-zinc-600 uppercase tracking-widest mb-1">Signal Lost</span>
          <p className="text-[10px] text-zinc-400 dark:text-zinc-700 uppercase tracking-tighter">Could not retrieve cinematic data</p>
        </div>
      )}

      {/* Main Cinematic Image */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt={`Cinematic Frame ${currentAttempt + 1}`}
          onLoad={() => setIsImageLoading(false)}
          onError={() => {
            setIsImageLoading(false);
            setHasImageError(true);
          }}
          className={`w-full h-full object-cover lg:object-contain transition-all duration-1000 ease-in-out ${
            isImageLoading || hasImageError ? 'opacity-0 scale-105 blur-lg' : 'opacity-100 scale-100 blur-0'
          }`}
        />
      )}

      {/* Cinematic Vignette Overlay */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.2)_100%)] opacity-60" />

      {/* UI Indicator overlay */}
      <div className="absolute bottom-3 lg:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 lg:gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-white/10 z-30 lg:scale-90">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
              i === currentAttempt ? 'bg-white scale-125 shadow-[0_0_8px_rgba(255,255,255,0.9)]' : 
              i < currentAttempt ? 'bg-emerald' : 'bg-white/20'
            }`}
          />
        ))}
      </div>
      
      {/* Frame Counter Tag */}
      <div className="absolute top-3 right-3 lg:top-4 lg:right-4 bg-black/70 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 shadow-lg z-30 lg:scale-90 origin-top-right min-w-fit">
        <span className="text-white font-heading text-[11px] lg:text-[10px] tracking-widest whitespace-nowrap">
          {TRANSLATIONS.reelLabel[lang]} {Math.min(currentAttempt + 1, 6)} <span className="text-white/40 mx-1">/</span> 6
        </span>
      </div>

      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default FrameDisplay;